import React, { useEffect, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';

import ButtonWithIcon from '../button/ButtonWithIcon';
import { IconClose16, IconClose24 } from '../icons/ic-close';
import { IconSearch16, IconSearch24 } from '../icons/ic-search';
import BasicInput, { BasicInputProps } from '../input/BasicInput';

export type SearchBarProps<TFieldName extends FieldValues = FieldValues> = {
    id?: string;
    className?: string;
    basicInputProps?: BasicInputProps<TFieldName>;
    label?: string;
    placeholder?: string;
    value?: string;
    leadingAction?: () => void;
    leadingActionIcon?: string | React.JSX.Element | StaticImageData;
    onChange?: (value: string) => void;
    onFocus?: (state: boolean) => void;
    onBlur?: () => void;
    onInputClick?: () => void;
    searchInputRef?: React.RefObject<HTMLInputElement>;
    customIcon?: React.JSX.Element;
    trailingIconSize?: '16' | '24';
    trailingIconColor?: string;
    showInput?: boolean;
};

function SearchBar<TFieldName extends FieldValues>(
    props: SearchBarProps<TFieldName>,
    ref: React.LegacyRef<HTMLDivElement> | undefined
): React.JSX.Element {
    const [lastSearchTimeout, setLastSearchTimeout] = useState<ReturnType<typeof setTimeout> | null | null>(null);
    const [animateIcon, setAnimateIcon] = useState(false);
    const [currentValue, setCurrentValue] = useState(props.value ?? undefined);

    const fallbackSearchInputRef = useRef<HTMLInputElement>(null);
    const searchInputRef = props.searchInputRef ?? fallbackSearchInputRef;

    function onSearchInput(input: string) {
        setCurrentValue(input);
        setAnimateIcon(!!input && input.length > 0);

        if (lastSearchTimeout !== null) {
            clearInterval(lastSearchTimeout);
            setLastSearchTimeout(null);
        }

        const timeOut = setInterval(() => {
            if (props.onChange) {
                props.onChange(input);
            }
            clearInterval(timeOut);
        }, 300);
        setLastSearchTimeout(timeOut);
    }

    function onLeadingIconClick() {
        if (props.leadingAction) {
            props.leadingAction();
        }
    }

    function onTrailingIconClick() {
        if (searchInputRef && searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setCurrentValue(undefined);
        props.onChange?.('');
    }

    function trailingIcon(): React.JSX.Element {
        const iconSearchClassName = `transform duration-150 ${animateIcon ? 'rotate-180 opacity-0' : ''} ${
            props.trailingIconColor
        }`;
        const iconCloseClassName = `absolute top-0 transform duration-150 ${animateIcon ? 'rotate-180' : 'opacity-0'} ${
            props.trailingIconColor
        }`;

        const size16: React.JSX.Element = (
            <div className="relative">
                <IconSearch16 className={iconSearchClassName} />
                <IconClose16 className={iconCloseClassName} />
            </div>
        );

        const size24: React.JSX.Element = (
            <div className="relative">
                <IconSearch24 className={iconSearchClassName} />
                <IconClose24 className={iconCloseClassName} />
            </div>
        );

        switch (props.trailingIconSize) {
            case '16':
                return size16;
            case '24':
                return size24;
            default:
                return size16;
        }
    }
    function handleClickOutside(event: Event) {
        if (event.target instanceof Node && !searchInputRef?.current?.contains(event.target)) {
            setAnimateIcon(false);
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            id={props.id}
            className={`${props.className} flex flex-row space-x-8`}
            ref={ref}
            onClick={props.onInputClick}>
            {props.leadingAction && (
                <ButtonWithIcon
                    icon={props.leadingActionIcon ?? ''}
                    onClick={onLeadingIconClick}
                    type="button"
                />
            )}
            <BasicInput
                {...props.basicInputProps}
                className="hide-search-input-clear-button"
                value={currentValue}
                label={props.label}
                title={props.placeholder}
                placeholder={props.placeholder}
                trailingIcon={props.customIcon ? props.customIcon : trailingIcon()}
                trailingIconAction={onTrailingIconClick}
                onChange={onSearchInput}
                onFocusChange={(state) => props.onFocus && props.onFocus(state)}
                type="search"
                inputRef={searchInputRef}
                onBlur={props.onBlur}
            />
        </div>
    );
}

export default React.forwardRef(SearchBar);
